import { classList } from '@findigs/helpers/src/helpers/class-list';
import type { ElementOrComponent } from '@findigs/helpers/src/helpers/polymorphic';
import type { FrameworkLinkProps } from '@findigs/helpers/src/hooks/use-framework-link';
import { useFrameworkLink } from '@findigs/helpers/src/hooks/use-framework-link';
import type { UseFrameworkLinkProps } from '@findigs/helpers/src/hooks/use-framework-link';
import { useMemo } from 'react';
import { styles, transitionDebounce } from './sidebar.module.scss';
import { useSidebarState } from './use-sidebar-state';

export type SidebarItem = FrameworkLinkProps & {
  title: string;
  href?: string;
  active?: boolean;
  disabled?: boolean;
};

export type SidebarProps<E extends ElementOrComponent> = Omit<
  React.HTMLProps<HTMLDivElement>,
  'as' | 'ref' | 'children'
> &
  UseFrameworkLinkProps<E> & {
    items: SidebarItem[];
  };

const transitionDebounceMs = parseFloat(transitionDebounce) * 1000;

/**
 * Extends the vanilla `nav` element with our styling.
 * All vanilla `nav` props are forwarded.
 */
export const Sidebar = <E extends ElementOrComponent>({
  className,
  style,
  items,
  frameworkLink,
  frameworkLinkProp,
  shouldUseFrameworkLink,
  ...props
}: SidebarProps<E>) => {
  const { FrameworkLink } = useFrameworkLink({
    frameworkLink,
    frameworkLinkProp,
    shouldUseFrameworkLink,
    disableStyles: true,
  });

  const active = useMemo(() => items.findIndex((item) => item.active), [items]);

  const { state, onHoverInside, onHoverOutside } = useSidebarState({
    transitionDebounceMs,
    items,
    activeIndex: active,
  });

  const classes = classList(
    styles,
    { ready: state.ready || active !== -1, hovered: state.hovered !== -1 },
    className
  );
  return (
    <nav
      {...props}
      className={classes}
      style={
        {
          ...style,
          '--count': items.length,
          '--hovered': state.hovered,
          '--active': active,
          '--fallback': active === -1 ? state.fallback : active,
        } as any
      }
    >
      <ul onMouseLeave={onHoverOutside}>
        {items.map(({ title, href, className, active, ...props }, i) => (
          <li key={href ?? i} onMouseEnter={() => onHoverInside(i)}>
            <FrameworkLink
              {...props}
              href={props.disabled || !href ? undefined : href}
              aria-disabled={
                !href || (props['aria-disabled'] ?? props.disabled) || undefined
              }
              className={classList('link', className)}
            >
              {title}
            </FrameworkLink>
          </li>
        ))}
      </ul>
    </nav>
  );
};
